import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import StandardLayout from '../../../components/layout/StandardLayout';
import { PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { loadQuestionsWithCategoriesAdmin, deleteQuestion } from '../../../services/questionAPIService';

const QuestionListAdmin = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);

    useEffect(() => {
        loadQuestionData();
    }, []);

    const loadQuestionData = async () => {
        try {
            setLoading(true);
            const data = await loadQuestionsWithCategoriesAdmin();
            setQuestions(data);
            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Fragen');
            console.error('Error loading questions:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (question) => {
        setDeleteConfirmation(question);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation) return;

        try {
            await deleteQuestion(deleteConfirmation.id);
            setQuestions(questions.filter(q => q.id !== deleteConfirmation.id));
            setDeleteConfirmation(null);
        } catch (err) {
            setError('Fehler beim Löschen der Frage');
            console.error('Error deleting question:', err);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation(null);
    };

    const ImageCell = ({ imageUrl }) => {
        if (!imageUrl) {
            return (
                <div className="flex justify-center">
                    <XMarkIcon className="w-5 h-5 text-error opacity-50" />
                </div>
            );
        }

        return (
            <div className="flex justify-center">
                <div className="relative w-10 h-10 group">
                    <img
                        src={imageUrl}
                        alt="Vorschau"
                        className="w-10 h-10 object-cover rounded shadow-sm cursor-pointer"
                    />
                    <div className="absolute inset-0 bg-base-100 opacity-0 group-hover:opacity-90 transition-opacity duration-200 flex items-center justify-center rounded">
                        <span className="text-xs">Anzeigen</span>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <StandardLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    if (error) {
        return (
            <StandardLayout>
                <div className="container mx-auto px-4 py-8">
                    <div className="alert alert-error">
                        <XMarkIcon className="w-6 h-6" />
                        <span>{error}</span>
                    </div>
                </div>
            </StandardLayout>
        );
    }

    return (
        <StandardLayout>
            <div className="container mx-auto px-4 py-8">
                {/* Delete Confirmation Modal */}
                {deleteConfirmation && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-base-100 p-6 rounded-lg shadow-xl max-w-md w-full">
                            <h3 className="text-lg font-bold mb-4">Frage löschen</h3>
                            <p className="mb-4">
                                Möchten Sie die folgende Frage wirklich löschen?
                                <br />
                                <span className="font-medium">{deleteConfirmation.question_text}</span>
                            </p>
                            <div className="flex justify-end gap-4">
                                <button
                                    className="btn btn-ghost"
                                    onClick={handleDeleteCancel}
                                >
                                    Abbrechen
                                </button>
                                <button
                                    className="btn btn-error"
                                    onClick={handleDeleteConfirm}
                                >
                                    Löschen
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Fragen Übersicht</h1>
                    <Link 
                        to="/admin/questions/add" 
                        className="btn btn-primary"
                    >
                        Neue Frage
                    </Link>
                </div>

                <div className="overflow-x-auto">
                    <table className="table table-zebra w-full">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Kategorie</th>
                                <th>Frage</th>
                                <th className="text-center">Frage Bild</th>
                                <th className="text-center">Antwort Bild</th>
                                <th className="text-center">Erklärungs Bild</th>
                                <th>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map((question) => (
                                <tr key={question.id} className="hover">
                                    <td className="whitespace-nowrap">
                                        {question.id}
                                    </td>
                                    <td className="whitespace-nowrap">
                                        <span className="badge badge-ghost">
                                            {question.category.name}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="max-w-md">
                                            <div className="font-medium truncate prose prose-sm">
                                                <ReactMarkdown>{question.question_text}</ReactMarkdown>
                                            </div>
                                            <div className="text-sm opacity-50 truncate prose prose-sm">
                                                Antwort: <ReactMarkdown className="inline">{question.answer_text}</ReactMarkdown>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <ImageCell imageUrl={question.question_image} />
                                    </td>
                                    <td>
                                        <ImageCell imageUrl={question.answer_image} />
                                    </td>
                                    <td>
                                        <ImageCell imageUrl={question.explanation_image} />
                                    </td>
                                    <td>
                                        <div className="flex gap-2">
                                            <Link 
                                                to={`/admin/questions/${question.id}/edit`}
                                                className="btn btn-square btn-sm btn-ghost"
                                                title="Bearbeiten"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </Link>
                                            <button 
                                                className="btn btn-square btn-sm btn-ghost text-error"
                                                title="Löschen"
                                                onClick={() => handleDeleteClick(question)}
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </StandardLayout>
    );
};

export default QuestionListAdmin; 